<script lang="ts">
	import { createQuery } from '@tanstack/svelte-query'
	import DetailedSelect from './DetailedSelect.svelte'
	import type { DetailedSelectItem } from '@repo/commons/models'
	import type { AiModelResponse } from '@semanticedge/knowledge-service-noheaders-client/src'
	import type { QueryKey } from '@tanstack/svelte-query'

	type FetchMode = {
		queryKey: QueryKey
		fetcher: () => Promise<AiModelResponse[]>
		items?: never
	}

	type ItemsMode = {
		items: DetailedSelectItem[]
		queryKey?: never
		fetcher?: never
	}

	type Props = (FetchMode | ItemsMode) & {
		selectedId?: string | null
		label?: string
		error?: string
		placeholder?: string
		clearable?: boolean
		disabled?: boolean
		isRequired?: boolean
		highlightPattern?: string
		displayIds?: boolean
		onBlur?: (() => void) | null
		onSelect?: ((item: DetailedSelectItem | null) => void) | null
		onHighlightMatch?: ((match: string, isExactMatch: boolean) => void) | undefined
	}

	let {
		queryKey,
		fetcher,
		items,
		selectedId = null,
		label = '',
		error = '',
		placeholder = 'Select AI model',
		clearable = false,
		disabled = false,
		isRequired = false,
		highlightPattern = '',
		displayIds = false,
		onBlur = null,
		onSelect = null,
		onHighlightMatch = undefined
	}: Props = $props()

	const isFetchMode = $derived(!!fetcher)

	const aiModelsQuery = $derived(
		isFetchMode
			? createQuery({
					queryKey: queryKey!,
					queryFn: () => fetcher!()
				})
			: null
	)

	const resolvedItems: DetailedSelectItem[] = $derived.by(() => {
		if (!isFetchMode) return items ?? []
		const models = $aiModelsQuery?.data ?? []
		return models.map((model: AiModelResponse) => ({
			id: model.id ?? '',
			title: model.name ?? '',
			description: model.description ?? ''
		}))
	})

	const isLoading = $derived(isFetchMode && !!$aiModelsQuery?.isLoading)

	const selectedItem = $derived(
		selectedId ? (resolvedItems.find((item) => item.id === selectedId) ?? null) : null
	)
</script>

<DetailedSelect
	items={resolvedItems}
	{label}
	{error}
	{isRequired}
	{placeholder}
	{clearable}
	{selectedItem}
	{onSelect}
	{onBlur}
	{highlightPattern}
	{displayIds}
	{onHighlightMatch}
	disabled={disabled || isLoading}
	{isLoading}
/>
